import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { ToolActivityItem } from "@/types";

/* ── Inline SVG Icons ── */

function SpinnerIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="animate-cx-spin cx-text-accent"
    >
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="cx-text-accent"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="cx-text-error"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/* ── Elapsed Timer ── */

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms / 100) * 100}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function ElapsedTimer({
  startedAt,
  completedAt,
}: {
  startedAt: number;
  completedAt?: number;
}) {
  const [elapsed, setElapsed] = useState(() =>
    completedAt ? completedAt - startedAt : Date.now() - startedAt,
  );

  useEffect(() => {
    if (completedAt) return;
    const id = setInterval(() => setElapsed(Date.now() - startedAt), 100);
    return () => clearInterval(id);
  }, [startedAt, completedAt]);

  return <span className="cx-tool-duration">{formatDuration(elapsed)}</span>;
}

/* ── Tool Activity Row ── */

function ToolActivityRow({ tool }: { tool: ToolActivityItem }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isExpandable = !!tool.summary;

  const label = t(`chat.toolNames.${tool.name}`, {
    defaultValue: tool.name.replace(/_/g, " "),
  });

  const statusIcon =
    tool.status === "running" ? (
      <SpinnerIcon />
    ) : tool.status === "error" ? (
      <ErrorIcon />
    ) : (
      <CheckIcon />
    );

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isExpandable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setExpanded((v) => !v);
    }
  }

  return (
    <div>
      <div
        className={`cx-tool-row cx-tool-row--${tool.status}${isExpandable ? " cx-tool-row--expandable" : ""}`}
        onClick={isExpandable ? () => setExpanded((v) => !v) : undefined}
        onKeyDown={handleKeyDown}
        role={isExpandable ? "button" : undefined}
        tabIndex={isExpandable ? 0 : undefined}
        aria-expanded={isExpandable ? expanded : undefined}
        aria-label={isExpandable ? `${label} — ${t(`chat.toolStatus.${tool.status}`)}` : undefined}
      >
        {isExpandable ? (
          <span
            className={`cx-tool-chevron${expanded ? " cx-tool-chevron--open" : ""}`}
            aria-hidden="true"
          >
            <ChevronRightIcon />
          </span>
        ) : (
          <span className="cx-tool-spacer" aria-hidden="true" />
        )}
        {statusIcon}
        <span className="cx-tool-label">{label}</span>
        {tool.context && <span className="cx-tool-context">{tool.context}</span>}
        <ElapsedTimer
          startedAt={tool.startedAt}
          completedAt={tool.completedAt}
        />
      </div>
      {isExpandable && (
        <div
          className={`cx-tool-body${expanded ? " cx-tool-body--open" : ""}`}
        >
          <div className="cx-tool-summary">{tool.summary}</div>
        </div>
      )}
    </div>
  );
}

/* ── Tool Activity (root) ── */

interface ToolActivityProps {
  tools: ToolActivityItem[];
}

export function ToolActivity({ tools }: ToolActivityProps) {
  if (tools.length === 0) return null;

  return (
    <div className="flex flex-col gap-0.5 animate-cx-fade-in">
      {tools.map((tool) => (
        <ToolActivityRow key={`${tool.name}-${tool.startedAt}`} tool={tool} />
      ))}
    </div>
  );
}
